export interface ProductionAudioLoadResult {
  readonly ok: boolean;
  readonly buffer?: AudioBuffer;
  readonly reason?: string;
}

export interface ProductionAudioLoaderSnapshot {
  readonly pending: number;
  readonly decoded: number;
  readonly disposed: boolean;
}

export type AudioFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export class ProductionAudioLoader {
  private readonly pending = new Map<string, Promise<ArrayBuffer>>();
  private readonly decoding = new Map<string, Promise<AudioBuffer>>();
  private readonly decoded = new Map<string, AudioBuffer>();
  private readonly abort = new AbortController();
  private disposed = false;

  constructor(private readonly fetchAudio: AudioFetch = globalThis.fetch.bind(globalThis)) {}

  get snapshot(): ProductionAudioLoaderSnapshot {
    return { pending: this.pending.size, decoded: this.decoded.size, disposed: this.disposed };
  }

  async load(context: BaseAudioContext, url: string): Promise<ProductionAudioLoadResult> {
    if (this.disposed) return { ok: false, reason: 'Audio loader is disposed.' };
    const cached = this.decoded.get(url);
    if (cached) return { ok: true, buffer: cached };
    try {
      let request = this.pending.get(url);
      if (!request) {
        request = this.fetchAudio(url, { signal: this.abort.signal }).then(async (response) => {
          if (!response.ok) throw new Error(`Audio request failed (${response.status}).`);
          return response.arrayBuffer();
        });
        this.pending.set(url, request);
      }
      const bytes = await request;
      if (this.disposed) return { ok: false, reason: 'Audio loader is disposed.' };
      let decoding = this.decoding.get(url);
      if (!decoding) {
        decoding = context.decodeAudioData(bytes.slice(0));
        this.decoding.set(url, decoding);
      }
      const buffer = await decoding;
      this.decoded.set(url, buffer);
      return { ok: true, buffer };
    } catch (error) {
      return {
        ok: false,
        reason: error instanceof Error ? error.message : 'Audio could not be loaded.',
      };
    } finally {
      this.pending.delete(url);
      this.decoding.delete(url);
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.abort.abort();
    this.pending.clear();
    this.decoding.clear();
    this.decoded.clear();
  }
}
